import {
	Directive,
	ElementRef,
	Injectable,
	effect,
	inject,
	input,
	signal,
	untracked,
	type Provider,
} from '@angular/core';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_NATIVE_DATE_FORMATS,
	NativeDateAdapter,
} from '@angular/material/core';

/** Picker-scoped first day of week for Material calendars (0 = Sunday, 1 = Monday). */
@Injectable()
export class WeekStartConfig {
	readonly firstDayOfWeek = signal<0 | 1>(0);

	setFirstDayOfWeek(day: 0 | 1): void {
		this.firstDayOfWeek.set(day);
	}
}

@Injectable()
export class JeptoWeekStartDateAdapter extends NativeDateAdapter {
	private readonly weekStartConfig = inject(WeekStartConfig);

	constructor() {
		super();
		// MatMonthView only re-reads weekday headers on DateAdapter.localeChanges.
		effect(() => {
			this.weekStartConfig.firstDayOfWeek();
			untracked(() => this._localeChanges.next());
		});
	}

	override getFirstDayOfWeek(): number {
		return this.weekStartConfig.firstDayOfWeek();
	}
}

/** Date adapter + formats for the picker; honors {@link WeekStartConfig}. */
export function provideJeptoWeekStartDateAdapter(): Provider[] {
	return [
		WeekStartConfig,
		{ provide: DateAdapter, useClass: JeptoWeekStartDateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
	];
}

/**
 * CDK overlay renders the panel outside the component DOM; {@link DateAdapter} must be
 * provided on the panel host so {@link MatCalendar} picks up {@link JeptoWeekStartDateAdapter}.
 */
@Directive({
	selector: '[jeptoDateRangePickerPanel]',
	providers: provideJeptoWeekStartDateAdapter(),
	exportAs: 'jeptoDateRangePickerPanel',
})
export class JeptoDateRangePickerPanelDirective {
	readonly hostElement = inject(ElementRef<HTMLElement>);
	readonly weekStartConfig = inject(WeekStartConfig);
	/** Sync from parent so mat-calendar reads the correct first day on first paint. */
	readonly firstDayOfWeek = input<0 | 1>(0);

	constructor() {
		effect(() => {
			this.weekStartConfig.setFirstDayOfWeek(this.firstDayOfWeek());
		});
	}
}
